import type { GameState } from '../game/GameState.ts';
import { EdgeLatch, noFlap, type Controller, type ControllerOutput } from './Controller.ts';

/**
 * Mouse / touch / on-screen button control.
 *
 * Listens for pointer-down on a specific element rather than on the window, so
 * clicking a dashboard control does not fly the bird. Pointer events cover
 * mouse, trackpad, touch, and pen with one code path, which matters because a
 * lab is as likely to have a big physical arcade button wired to a USB HID
 * device as it is to have a mouse.
 */
export class MouseController implements Controller {
  readonly id = 'mouse';
  readonly label = 'Mouse / on-screen button';

  private latch = new EdgeLatch();
  private attached = false;
  private pressCount = 0;
  private handler = (e: PointerEvent) => this.onPointerDown(e);

  constructor(private readonly target: HTMLElement) {}

  attach(): void {
    if (this.attached) return;
    this.target.addEventListener('pointerdown', this.handler);
    this.attached = true;
  }

  detach(): void {
    if (!this.attached) return;
    this.target.removeEventListener('pointerdown', this.handler);
    this.latch.clear();
    this.attached = false;
  }

  reset(): void {
    this.latch.clear();
  }

  update(_state: GameState): ControllerOutput {
    const edge = this.latch.consume();
    if (!edge) return noFlap();
    return { flap: true, timestamp: edge.timestamp, metadata: { source: 'pointer' } };
  }

  status(): Record<string, unknown> {
    return { presses: this.pressCount };
  }

  private onPointerDown(e: PointerEvent): void {
    e.preventDefault();
    this.pressCount++;
    this.latch.set(performance.now(), { pointerType: e.pointerType });
  }
}
